// Gemensamt dokumenttema för alla Word-exporter.
// Alla generatorer använder dessa byggblock så att exporterna ser likadana ut:
// samma sidformat, typsnitt, rubriker, tabeller, sidhuvud och sidfot.
import {
  AlignmentType,
  BorderStyle,
  Document,
  Footer,
  Header,
  HeadingLevel,
  ISectionOptions,
  PageNumber,
  PageOrientation,
  Paragraph,
  ShadingType,
  Table,
  TableCell,
  TableRow,
  TableOfContents,
  TextRun,
  WidthType,
} from "docx";
import { stampText, type DocumentStamp } from "./watermark";

/* ---------- grundmått ---------- */

/** A4 i DXA (1440 = 1 tum). */
export const PAGE_WIDTH = 11906;
export const PAGE_HEIGHT = 16838;
/** Marginal vald så att innehållsbredden blir jämna 9360 DXA. */
export const PAGE_MARGIN = 1273;
export const CONTENT_WIDTH = 9360;
/** Innehållsbredd i liggande format. */
export const CONTENT_WIDTH_LANDSCAPE = PAGE_HEIGHT - PAGE_MARGIN * 2;

/* ---------- färger och typografi ---------- */

export const COLORS = {
  text: "1A1A1A",
  heading: "1F3864",
  muted: "666666",
  faint: "999999",
  line: "B7C4D4",
  tableBorder: "BFBFBF",
  tableHeader: "E8EEF5",
  tableZebra: "F6F8FB",
};

const FONT = "Arial";

/** Word anger storlek i halva punkter: 22 = 11 pt. */
export const docStyles = {
  default: {
    document: { run: { font: FONT, size: 22, color: COLORS.text } },
  },
  paragraphStyles: [
    {
      id: "Title",
      name: "Title",
      basedOn: "Normal",
      next: "Normal",
      quickFormat: true,
      run: { font: FONT, size: 40, bold: true, color: COLORS.heading },
      paragraph: { spacing: { after: 120 } },
    },
    {
      id: "Heading1",
      name: "Heading 1",
      basedOn: "Normal",
      next: "Normal",
      quickFormat: true,
      run: { font: FONT, size: 28, bold: true, color: COLORS.heading },
      paragraph: { spacing: { before: 320, after: 140 }, outlineLevel: 0 },
    },
    {
      id: "Heading2",
      name: "Heading 2",
      basedOn: "Normal",
      next: "Normal",
      quickFormat: true,
      run: { font: FONT, size: 24, bold: true, color: COLORS.heading },
      paragraph: { spacing: { before: 240, after: 100 }, outlineLevel: 1 },
    },
    {
      id: "Heading3",
      name: "Heading 3",
      basedOn: "Normal",
      next: "Normal",
      quickFormat: true,
      run: { font: FONT, size: 22, bold: true, color: COLORS.text },
      paragraph: { spacing: { before: 180, after: 80 }, outlineLevel: 2 },
    },
  ],
};

/* ---------- värden ---------- */

/** Tomt värde visas alltid likadant. */
export const val = (v: unknown): string => {
  if (v === null || v === undefined) return "–";
  const text = String(v).trim();
  return text.length ? text : "–";
};

/** Datum visas alltid som ÅÅÅÅ-MM-DD. */
export const fmtDate = (iso?: string | null): string => {
  if (!iso) return "–";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return String(iso);
  return d.toLocaleDateString("sv-SE");
};

export const fmtDateTime = (iso?: string | null): string => {
  if (!iso) return "–";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return String(iso);
  return d.toLocaleString("sv-SE", { dateStyle: "short", timeStyle: "short" });
};

/* ---------- text ---------- */

export const title = (text: string) =>
  new Paragraph({ heading: HeadingLevel.TITLE, children: [new TextRun({ text })] });

export const h1 = (text: string) =>
  new Paragraph({ heading: HeadingLevel.HEADING_1, children: [new TextRun({ text })] });

export const h2 = (text: string) =>
  new Paragraph({ heading: HeadingLevel.HEADING_2, children: [new TextRun({ text })] });

export const h3 = (text: string) =>
  new Paragraph({ heading: HeadingLevel.HEADING_3, children: [new TextRun({ text })] });

export const body = (text: string, opts: { bold?: boolean; italics?: boolean; muted?: boolean } = {}) =>
  new Paragraph({
    spacing: { after: 120 },
    children: String(text ?? "")
      .split("\n")
      .map(
        (line, i) =>
          new TextRun({
            text: line,
            bold: opts.bold,
            italics: opts.italics,
            color: opts.muted ? COLORS.muted : undefined,
            break: i > 0 ? 1 : undefined,
          })
      ),
  });

export const bullet = (text: string) =>
  new Paragraph({ text, bullet: { level: 0 }, spacing: { after: 60 } });

export const spacer = (size = 200) => new Paragraph({ spacing: { after: size }, children: [] });

/* ---------- tabeller ---------- */

const tableBorder = { style: BorderStyle.SINGLE, size: 1, color: COLORS.tableBorder };
export const cellBorders = {
  top: tableBorder,
  bottom: tableBorder,
  left: tableBorder,
  right: tableBorder,
};
export const cellMargins = { top: 90, bottom: 90, left: 130, right: 130 };

export const cell = (
  text: string,
  width: number,
  opts: { bold?: boolean; shaded?: boolean; zebra?: boolean; align?: (typeof AlignmentType)[keyof typeof AlignmentType] } = {}
) =>
  new TableCell({
    borders: cellBorders,
    margins: cellMargins,
    width: { size: width, type: WidthType.DXA },
    shading: opts.shaded
      ? { fill: COLORS.tableHeader, type: ShadingType.CLEAR, color: "auto" }
      : opts.zebra
        ? { fill: COLORS.tableZebra, type: ShadingType.CLEAR, color: "auto" }
        : undefined,
    children: String(text ?? "")
      .split("\n")
      .map(
        (line) =>
          new Paragraph({
            alignment: opts.align,
            spacing: { after: 0 },
            children: [new TextRun({ text: line, bold: opts.bold })],
          })
      ),
  });

const spread = (count: number, totalWidth: number) => {
  const widths = Array.from({ length: count }, () => Math.floor(totalWidth / count));
  widths[0] += totalWidth - widths.reduce((a, b) => a + b, 0);
  return widths;
};

/**
 * Standardtabell: rubrikrad med fyllning, radvis zebra och samma ramar överallt.
 * Tom tabell renderas med en tydlig rad i stället för att försvinna.
 */
export const table = (
  headers: string[],
  rows: (string | null | undefined)[][],
  opts: { columnWidths?: number[]; totalWidth?: number; zebra?: boolean } = {}
) => {
  const totalWidth = opts.totalWidth ?? CONTENT_WIDTH;
  const widths = opts.columnWidths ? [...opts.columnWidths] : spread(headers.length, totalWidth);
  widths[0] += totalWidth - widths.reduce((a, b) => a + b, 0);
  const zebra = opts.zebra !== false;

  const dataRows = rows.length
    ? rows
    : [[...Array(headers.length)].map((_, i) => (i === 0 ? "Inga uppgifter registrerade" : ""))];

  return new Table({
    width: { size: totalWidth, type: WidthType.DXA },
    columnWidths: widths,
    rows: [
      new TableRow({
        tableHeader: true,
        children: headers.map((header, i) => cell(header, widths[i], { bold: true, shaded: true })),
      }),
      ...dataRows.map(
        (row, rowIndex) =>
          new TableRow({
            children: widths.map((width, i) =>
              cell(val(row[i]), width, { zebra: zebra && rowIndex % 2 === 1 })
            ),
          })
      ),
    ],
  });
};

/** Två kolumner: etikett och värde. Används för uppgiftsrutor. */
export const factTable = (
  rows: [string, string | null | undefined][],
  opts: { totalWidth?: number } = {}
) => {
  const totalWidth = opts.totalWidth ?? CONTENT_WIDTH;
  const labelWidth = Math.round(totalWidth * 0.32);
  const valueWidth = totalWidth - labelWidth;
  return new Table({
    width: { size: totalWidth, type: WidthType.DXA },
    columnWidths: [labelWidth, valueWidth],
    rows: rows.map(
      ([label, value]) =>
        new TableRow({
          children: [
            cell(label, labelWidth, { bold: true, shaded: true }),
            cell(val(value), valueWidth),
          ],
        })
    ),
  });
};

/** Innehållsförteckning för de längre dokumenten. */
export const tableOfContents = () => [
  h1("Innehåll"),
  body("Uppdatera fältet i Word (Ctrl+A, F9) för aktuella sidnummer.", { muted: true, italics: true }),
  new TableOfContents("Innehåll", { hyperlink: true, headingStyleRange: "1-2" }),
  spacer(240),
];

/* ---------- försättsdel ---------- */

/** Granskningsstatus som visas på försättsbladet. */
export interface DocReviewInfo {
  status?: string;
  approved?: boolean;
  rows?: string[][];
}

export interface DocMeta {
  /** Dokumentnummer enligt Bas-processen, t.ex. "12" */
  docNumber?: string | null;
  documentName: string;
  projectName?: string | null;
  version?: number | string | null;
  status?: string | null;
  createdBy?: string | null;
  date?: string | null;
  /** Extra rader sist i försättstabellen */
  extra?: [string, string | null | undefined][];
  /** Granskningsstatus – styr status på försättsbladet och utkastrutan */
  review?: DocReviewInfo;
}

const docLabel = (meta: DocMeta) => (meta.docNumber ? `Dok ${meta.docNumber}` : "");

/** Titel + försättstabell som inleder varje dokument. */
export const coverBlock = (meta: DocMeta, stamp?: DocumentStamp | null): (Paragraph | Table)[] => {
  const label = docLabel(meta);
  return [
    ...(label
      ? [
          new Paragraph({
            spacing: { after: 40 },
            children: [
              new TextRun({ text: label.toUpperCase(), bold: true, size: 18, color: COLORS.muted }),
            ],
          }),
        ]
      : []),
    title(meta.documentName),
    new Paragraph({
      spacing: { after: 240 },
      border: { bottom: { style: BorderStyle.SINGLE, size: 6, color: COLORS.line, space: 6 } },
      children: [],
    }),
    factTable([
      ["Projekt", meta.projectName],
      ["Dokument", label ? `${label} – ${meta.documentName}` : meta.documentName],
      ["Version", meta.version === null || meta.version === undefined ? "1" : String(meta.version)],
      ["Status", meta.status || meta.review?.status],
      ["Upprättad av", meta.createdBy || stamp?.exportedBy],
      ["Datum", meta.date ? fmtDate(meta.date) : fmtDate(new Date().toISOString())],
      ...(meta.extra ?? []),
      ...(stamp ? ([["Verifieringskod", stamp.code]] as [string, string][]) : []),
    ]),
    spacer(240),
  ];
};

/* ---------- sidhuvud och sidfot ---------- */

export const themeHeader = (meta: DocMeta): Header => {
  const label = docLabel(meta);
  return new Header({
    children: [
      new Paragraph({
        tabStops: [{ type: "right" as never, position: CONTENT_WIDTH_LANDSCAPE }],
        border: { bottom: { style: BorderStyle.SINGLE, size: 4, color: COLORS.line, space: 4 } },
        children: [
          new TextRun({ text: meta.projectName || "Byggarbetsmiljökontrollen", size: 16, color: COLORS.muted }),
          new TextRun({
            text: `\t${label ? `${label} – ` : ""}${meta.documentName}`,
            size: 16,
            color: COLORS.muted,
          }),
        ],
      }),
      new Paragraph({ spacing: { after: 60 }, children: [] }),
    ],
  });
};

export const themeFooter = (stamp?: DocumentStamp | null): Footer =>
  new Footer({
    children: [
      new Paragraph({
        alignment: AlignmentType.CENTER,
        spacing: { before: 60 },
        children: [
          new TextRun({ text: "Sida ", size: 16, color: COLORS.muted }),
          new TextRun({ children: [PageNumber.CURRENT], size: 16, color: COLORS.muted }),
          new TextRun({ text: " av ", size: 16, color: COLORS.muted }),
          new TextRun({ children: [PageNumber.TOTAL_PAGES], size: 16, color: COLORS.muted }),
        ],
      }),
      ...(stamp
        ? [
            new Paragraph({
              alignment: AlignmentType.CENTER,
              children: [new TextRun({ text: stampText(stamp), size: 14, color: COLORS.faint })],
            }),
            new Paragraph({
              alignment: AlignmentType.CENTER,
              children: [
                new TextRun({
                  text: "Dokumentet gäller endast för projektet ovan. Kontrollera koden på arbetsmiljokontrollen.se/verifiera",
                  size: 14,
                  color: COLORS.faint,
                  italics: true,
                }),
              ],
            }),
          ]
        : []),
      new Paragraph({
        alignment: AlignmentType.CENTER,
        children: [
          new TextRun({
            text: "© Byggarbetsmiljökontrollen – mall och struktur får inte kopieras eller säljas vidare.",
            size: 12,
            color: COLORS.faint,
          }),
        ],
      }),
    ],
  });

/** Ruta som visar att dokumentet ännu inte är godkänt för överlämning. */
export const draftNotice = () =>
  new Paragraph({
    spacing: { before: 120, after: 240 },
    shading: { fill: "FFF4E5", type: ShadingType.CLEAR, color: "auto" },
    border: {
      top: { style: BorderStyle.SINGLE, size: 6, color: "E0A96D", space: 6 },
      bottom: { style: BorderStyle.SINGLE, size: 6, color: "E0A96D", space: 6 },
      left: { style: BorderStyle.SINGLE, size: 6, color: "E0A96D", space: 6 },
      right: { style: BorderStyle.SINGLE, size: 6, color: "E0A96D", space: 6 },
    },
    children: [
      new TextRun({
        text: "UTKAST – ej godkänt för överlämning. Dokumentet saknar sign-off från granskare.",
        bold: true,
        size: 18,
        color: "8A5A00",
      }),
    ],
  });

/* ---------- underskrifter ---------- */

export const signatureBlock = (roles: string[]): (Paragraph | Table)[] => [
  h1("Underskrifter"),
  table(
    ["Roll", "Namn", "Datum", "Underskrift"],
    roles.map((role) => [role, "", "", ""]),
    { columnWidths: [2400, 2600, 1600, 2760], zebra: false }
  ),
];

/* ---------- dokument ---------- */

export interface BuildDocumentOptions {
  meta: DocMeta;
  children: (Paragraph | Table)[];
  stamp?: DocumentStamp | null;
  landscape?: boolean;
  /** Utelämna försättsdelen när dokumentet bygger sin egen (t.ex. AMP). */
  skipCover?: boolean;
}

/** Bygger ett Word-dokument med gemensamt sidformat, sidhuvud, sidfot och försättsdel. */
export const buildDocument = ({
  meta,
  children,
  stamp,
  landscape,
  skipCover,
}: BuildDocumentOptions): Document => {
  const section: ISectionOptions = {
    properties: {
      page: {
        size: landscape
          ? { width: PAGE_WIDTH, height: PAGE_HEIGHT, orientation: PageOrientation.LANDSCAPE }
          : { width: PAGE_WIDTH, height: PAGE_HEIGHT },
        margin: {
          top: PAGE_MARGIN,
          right: PAGE_MARGIN,
          bottom: PAGE_MARGIN,
          left: PAGE_MARGIN,
        },
      },
    },
    headers: { default: themeHeader(meta) },
    footers: { default: themeFooter(stamp) },
    children: [
      ...(skipCover ? [] : coverBlock(meta, stamp)),
      ...(meta.review && !meta.review.approved ? [draftNotice()] : []),
      ...children,
    ],
  };

  return new Document({
    creator: "Byggarbetsmiljökontrollen",
    title: meta.docNumber ? `Dok ${meta.docNumber} – ${meta.documentName}` : meta.documentName,
    description: meta.projectName || undefined,
    styles: docStyles,
    numbering: {
      config: [
        {
          reference: "amk-bullets",
          levels: [
            {
              level: 0,
              format: "bullet" as never,
              text: "•",
              alignment: AlignmentType.LEFT,
              style: { paragraph: { indent: { left: 720, hanging: 360 } } },
            },
          ],
        },
      ],
    },
    sections: [section],
  });
};
